import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { X } from 'lucide-react'

const stepsByRole = {
  admin: [
    {
      target: 'welcome',
      title: 'Bem-vindo ao Parkko!',
      content:
        'Vamos fazer um tour rápido para você conhecer as principais funcionalidades da gestão do seu estacionamento.',
    },
    {
      target: 'tour-lot-selector',
      title: 'Múltiplas Unidades',
      content:
        'Aqui você pode alternar rapidamente entre os diferentes estacionamentos que você gerencia.',
    },
    {
      target: 'tour-nav-management',
      title: 'Gestão Central',
      content:
        'Neste menu você cadastra novos operadores, configura permissões e gerencia suas unidades de estacionamento.',
    },
    {
      target: 'tour-nav-pricing',
      title: 'Regras de Preço',
      content:
        'Configure tarifas para diferentes veículos, horários de pico e limites diários.',
    },
    {
      target: 'tour-nav-reports',
      title: 'Visão Financeira',
      content:
        'Acompanhe de perto o faturamento, ticket médio e a saúde financeira do seu negócio.',
    },
  ],
  ultramaster: [
    {
      target: 'welcome',
      title: 'Bem-vindo ao Parkko!',
      content:
        'Vamos fazer um tour rápido para você conhecer as principais funcionalidades da gestão do seu estacionamento.',
    },
    {
      target: 'tour-lot-selector',
      title: 'Múltiplas Unidades',
      content:
        'Aqui você pode alternar rapidamente entre os diferentes estacionamentos que você gerencia.',
    },
    {
      target: 'tour-nav-management',
      title: 'Gestão Central',
      content:
        'Neste menu você cadastra novos operadores, configura permissões e gerencia suas unidades de estacionamento.',
    },
    {
      target: 'tour-nav-pricing',
      title: 'Regras de Preço',
      content:
        'Configure tarifas para diferentes veículos, horários de pico e limites diários.',
    },
    {
      target: 'tour-nav-reports',
      title: 'Visão Financeira',
      content:
        'Acompanhe de perto o faturamento, ticket médio e a saúde financeira do seu negócio.',
    },
  ],
  operator: [
    {
      target: 'welcome',
      title: 'Bem-vindo ao Parkko!',
      content:
        'Este é o seu painel de operação. Vamos ver como registrar veículos e fazer cobranças de forma ágil.',
    },
    {
      target: 'tour-nav-dashboard',
      title: 'Entrada de Veículos',
      content:
        'Aqui você registra o check-in de novos veículos e acompanha o status do pátio.',
    },
    {
      target: 'tour-nav-checkout',
      title: 'Saída e Pagamento',
      content:
        'Use esta tela para buscar placas, calcular o tempo de permanência e registrar os pagamentos.',
    },
    {
      target: 'tour-nav-map',
      title: 'Mapa de Vagas',
      content:
        'Visualize a ocupação do estacionamento em tempo real e identifique vagas livres rapidamente.',
    },
  ],
  financial: [
    {
      target: 'welcome',
      title: 'Bem-vindo ao Parkko!',
      content:
        'Seu painel financeiro está pronto. Vamos conhecer as ferramentas de relatórios e gestão de caixa.',
    },
    {
      target: 'tour-nav-reports',
      title: 'Relatórios Avançados',
      content:
        'Acesse os dados de faturamento, exporte para planilhas e analise os recebimentos.',
    },
    {
      target: 'tour-nav-cashier',
      title: 'Fechamento de Caixa',
      content:
        'Confira os turnos dos operadores, sangrias e faça a conciliação de valores.',
    },
  ],
}

export function Onboarding() {
  const { profile, completeOnboarding } = useAuth()
  const [currentStep, setCurrentStep] = useState(0)
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null)
  const [isVisible, setIsVisible] = useState(false)

  const steps = profile ? stepsByRole[profile.role] || stepsByRole.operator : []

  useEffect(() => {
    if (profile && profile.has_completed_onboarding === false) {
      setIsVisible(true)
    }
  }, [profile])

  const handleComplete = async () => {
    setIsVisible(false)
    await completeOnboarding()
  }

  useEffect(() => {
    if (!isVisible || currentStep === 0) return

    const targetId = steps[currentStep]?.target
    if (!targetId) return

    const updateRect = () => {
      const elements = document.querySelectorAll(`[data-tour="${targetId}"]`)
      let visibleEl = null
      for (let i = 0; i < elements.length; i++) {
        const rect = elements[i].getBoundingClientRect()
        if (rect.width > 0 && rect.height > 0) {
          visibleEl = elements[i]
          break
        }
      }
      if (visibleEl) {
        setTargetRect(visibleEl.getBoundingClientRect())
      } else {
        setTargetRect(null)
      }
    }

    updateRect()
    window.addEventListener('resize', updateRect)
    const interval = setInterval(updateRect, 500)

    return () => {
      window.removeEventListener('resize', updateRect)
      clearInterval(interval)
    }
  }, [currentStep, isVisible, steps])

  if (!isVisible) return null

  const step = steps[currentStep]

  if (currentStep === 0) {
    return (
      <Dialog open={true} onOpenChange={(open) => !open && handleComplete()}>
        <DialogContent className="sm:max-w-md z-[200]">
          <DialogHeader>
            <DialogTitle className="text-2xl">{step.title}</DialogTitle>
            <DialogDescription className="pt-2 text-base text-muted-foreground">
              {step.content}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6 flex flex-row sm:justify-between justify-between w-full gap-2">
            <Button variant="ghost" onClick={handleComplete}>
              Pular Tour
            </Button>
            <Button onClick={() => setCurrentStep(1)}>Começar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  const isNearBottom = targetRect
    ? targetRect.bottom + 200 > window.innerHeight
    : false

  return (
    <div className="fixed inset-0 z-[150] pointer-events-auto select-none">
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {/* Cutout Backdrop */}
        {targetRect && (
          <div
            className="absolute rounded-lg transition-all duration-300 shadow-[0_0_0_9999px_rgba(0,0,0,0.7)]"
            style={{
              top: targetRect.top - 6,
              left: targetRect.left - 6,
              width: targetRect.width + 12,
              height: targetRect.height + 12,
            }}
          />
        )}
        {!targetRect && (
          <div className="absolute inset-0 bg-black/70 transition-all duration-300" />
        )}

        {/* Tooltip Card */}
        <div
          className="absolute z-[151] bg-card text-card-foreground p-5 rounded-xl shadow-2xl w-[90%] max-w-sm pointer-events-auto border animate-in fade-in zoom-in duration-300"
          style={
            targetRect
              ? {
                  top: isNearBottom
                    ? targetRect.top - 180
                    : targetRect.bottom + 16,
                  left: Math.min(
                    Math.max(16, targetRect.left),
                    window.innerWidth - 336,
                  ),
                }
              : {
                  bottom: 80,
                  left: '50%',
                  transform: 'translateX(-50%)',
                }
          }
        >
          <button
            onClick={handleComplete}
            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>

          <h4 className="font-bold text-lg mb-2 pr-6 text-foreground">
            {step.title}
          </h4>
          <p className="text-sm text-muted-foreground mb-6">
            {!targetRect
              ? 'Abra o Menu Principal para encontrar esta opção. '
              : ''}
            {step.content}
          </p>

          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground font-medium bg-muted px-2 py-1 rounded-md">
              {currentStep} de {steps.length - 1}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentStep((c) => c - 1)}
              >
                Voltar
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  if (currentStep < steps.length - 1)
                    setCurrentStep((c) => c + 1)
                  else handleComplete()
                }}
              >
                {currentStep === steps.length - 1 ? 'Concluir' : 'Próximo'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
